import { View } from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import type { ReactElement } from 'react';

import Enums from '@core/types/api/Enums';
import type { Quote } from '@core/types/api/Quote/Quote';

import { PdfTextBlock } from '../../../components/PdfTextBlock';
import { QuotePdf } from '../../../QuotePdf';
import { PdfNotes } from '../../components/PdfNotes';
import { PdfProductsTable } from '../../components/PdfProductsTable';
import { PdfSpacer } from '../../components/PdfSpacer';
import { PdfHeaderTable } from '../components/PdfHeaderTable';
import { PdfSignatures } from '../components/PdfSignatures';

const viewStyle: Style = {
  textAlign: 'center',
};

const customUnitLabels = (label: string): string => {
  switch (label) {
    case 'CUYD':
      return '/cy';
    case '/CUYD':
      return '/cy';
    case 'tn':
      return '/tn';
    case '/tn':
      return '/tn';
    default:
      return label;
  }
};

type CarraraAggregateTemplateProps = {
  quote: Quote;
  locale: string;
};

export const CarraraAggregateTemplate = ({
  quote,
  locale,
}: CarraraAggregateTemplateProps): ReactElement => (
  <QuotePdf quote={quote} disableHyphenation>
    <PdfHeaderTable quote={quote} locale={locale} />
    <PdfSpacer height='0.25in' />
    <PdfTextBlock
      content='Aggregate'
      viewStyle={{
        alignItems: 'center',
      }}
    />
    <PdfSpacer height='0.1in' />
    <PdfProductsTable
      products={quote.filterProducts(Enums.QuoteProductKind.Primary)}
      displayHeaders
      columnConfigs={[
        {
          id: 'name',
          label: 'Product Description',
          type: 'string',
          headerViewStyle: viewStyle,
          rowViewStyle: viewStyle,
          width: '60%',
        },
        {
          id: 'price',
          label: 'Price',
          type: 'Currency',
          headerViewStyle: viewStyle,
          rowViewStyle: viewStyle,
          width: '20%',
        },
        {
          id: 'measurementUnit',
          label: 'Unit',
          type: 'string',
          headerViewStyle: viewStyle,
          rowViewStyle: viewStyle,
          width: '20%',
          formatValueToString: customUnitLabels,
        },
      ]}
      striped
      bordered
    />
    <PdfSpacer height='0.25in' />
    <PdfTextBlock
      content='Additional Products and Services'
      viewStyle={{
        alignItems: 'center',
      }}
    />
    <PdfSpacer height='0.1in' />
    <PdfProductsTable
      products={quote.filterProducts(Enums.QuoteProductKind.Additional)}
      displayHeaders
      columnConfigs={[
        {
          id: 'name',
          label: 'Product Description',
          type: 'string',
          headerViewStyle: viewStyle,
          rowViewStyle: viewStyle,
          width: '60%',
        },
        {
          id: 'price',
          label: 'Price',
          type: 'Currency',
          headerViewStyle: viewStyle,
          rowViewStyle: viewStyle,
          width: '20%',
        },
        {
          id: 'measurementUnit',
          label: 'Unit',
          type: 'string',
          headerViewStyle: viewStyle,
          rowViewStyle: viewStyle,
          width: '20%',
          formatValueToString: customUnitLabels,
        },
      ]}
      striped
      bordered
    />
    <PdfSpacer height='0.25in' />
    <PdfTextBlock
      content='HOURS OF OPERATION: Normal business hours are 7 am to 4 pm Monday - Friday. Delivery outside of these hours will incur extra charges.'
      textStyle={{
        fontFamily: 'OpenSans',
        fontWeight: 'bold',
        fontSize: '8pt',
      }}
    />
    <PdfSpacer height='0.1in' />
    <PdfSpacer height='0.25in' />
    {quote.terms !== null && (
      <View>
        <PdfNotes quote={quote} fontSize={10} />
        <PdfSpacer height='0.25in' />
      </View>
    )}
    <PdfSignatures quote={quote} />
    <PdfSpacer height='0.25in' />
    <PdfTextBlock
      content='A SIGNED AND DATED COPY OF THIS QUOTE MUST BE RETURNED WITH THE PURCHASE ORDER'
      textStyle={{
        fontSize: '9pt',
        textAlign: 'center',
      }}
    />
    <PdfSpacer height='0.1in' />
    <PdfTextBlock
      content='MAIL PURCHASE ORDER TO: 2464 CASE STREET - MIDDLEBURY, VT 05753'
      textStyle={{
        fontSize: '9pt',
        textAlign: 'center',
      }}
    />
  </QuotePdf>
);
